import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaFilePdf, FaDownload, FaFolder, FaFilter } from 'react-icons/fa';
import axiosPost from '../../helper/axios-helper';
import '../../styles/WhitePapers.css';

function WhitePapers() {
  const [selectedYear, setSelectedYear] = useState('all');
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosPost('white_papers/fetch', {}, {}, (data) => {
      if (data.success) {
        setPapers(data.data || []);
      }
      setLoading(false);
    });
  }, []);

  // Group papers by committee
  const whitePapers = papers.reduce((acc, paper) => {
    const key = paper.committee || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(paper);
    return acc;
  }, {});

  const getFileIcon = (fileType) => {
    if (fileType && fileType.includes('pdf')) {
      return <FaFilePdf className="file-icon pdf" />;
    }
    return <FaFileAlt className="file-icon" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getAvailableYears = (committeeKey) => {
    const years = whitePapers[committeeKey].map(paper => paper.year);
    return ['all', ...new Set(years)].sort((a, b) => {
      if (a === 'all') return -1;
      if (b === 'all') return 1;
      return b - a;
    });
  };

  const filterPapersByYear = (paperList) => {
    if (selectedYear === 'all') return paperList;
    return paperList.filter(paper => paper.year === selectedYear);
  };

  return (
    <div className="white-papers-container">
      <div className="white-papers-header">
        <div className="white-papers-header-content">
          <h1>White Papers</h1>
          <div className="white-papers-divider"></div>
          <p className="white-papers-description">
            Access CANTO's comprehensive white papers covering critical topics in telecommunications, 
            digital policy, and regulatory frameworks across the Caribbean region.
          </p>
        </div>
      </div>

      <div className="white-papers-content">
        {loading ? (
          <div className="white-papers-loading">
            <div className="loading-spinner"></div>
            <p>Loading white papers...</p>
          </div>
        ) : Object.keys(whitePapers).length === 0 ? (
          <div className="no-papers-message">
            <FaFolder style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
            <p>No white papers available at this time. Check back soon.</p>
          </div>
        ) : (
          Object.keys(whitePapers).map((committee) => (
            <div key={committee} className="white-papers-committee">
              <div className="committee-header">
                <h2 className="committee-title">
                  <FaFolder className="committee-icon" />
                  {committee}
                </h2>
                
                <div className="year-filter">
                  <FaFilter className="filter-icon" />
                  <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="year-select"
                  >
                    {getAvailableYears(committee).map(year => (
                      <option key={year} value={year}>
                        {year === 'all' ? 'All Years' : year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="documents-list">
                {filterPapersByYear(whitePapers[committee]).map((paper) => (
                  <a 
                    key={paper.id} 
                    href={paper.file_path} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="document-card"
                  >
                    <div className="document-icon">
                      {getFileIcon(paper.file_type)}
                    </div>
                    <div className="document-info">
                      <div className="document-year-badge">{paper.year}</div>
                      <h3 className="document-title">{paper.title}</h3>
                      <div className="document-meta">
                        <span className="document-filename">{paper.file_name}</span>
                        {paper.file_size && (
                          <span className="document-size">{formatFileSize(paper.file_size)}</span>
                        )}
                      </div>
                    </div>
                    <div className="document-download">
                      <FaDownload />
                    </div>
                  </a>
                ))}
              </div>

              {filterPapersByYear(whitePapers[committee]).length === 0 && (
                <div className="no-papers-message">
                  <p>No white papers available for {selectedYear}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default WhitePapers;
